/**
 * FASTA + FASTQ parsing.
 * Pure-functional, streaming-free at v0.1 (suitable for reference
 * genomes up to ~30Mb; for SRA-scale data the v0.3 streaming variant
 * lands as a separate path).
 */

export interface FastaRecord {
  /** First whitespace-delimited word of the header. */
  id: string;
  /** Full header line minus the leading '>'. */
  header: string;
  /** Single-line sequence, uppercase. */
  sequence: string;
}

export interface FastqRecord {
  id: string;
  header: string;
  sequence: string;
  /** Quality string, same length as sequence. */
  quality: string;
}

/**
 * Parse a FASTA-format string. Multi-line sequences are joined.
 * Sequence is upper-cased and whitespace stripped.
 */
export function parseFasta(text: string): FastaRecord[] {
  const records: FastaRecord[] = [];
  let header: string | null = null;
  let buf: string[] = [];
  const flush = () => {
    if (header === null) return;
    const sequence = buf.join('').replace(/\s+/g, '').toUpperCase();
    const id = header.split(/\s+/, 1)[0] ?? '';
    records.push({ id, header, sequence });
    buf = [];
  };
  for (const line of text.split(/\r?\n/)) {
    if (line.startsWith('>')) {
      flush();
      header = line.slice(1).trim();
    } else if (header !== null && line.length > 0) {
      buf.push(line);
    }
  }
  flush();
  return records;
}

/**
 * Serialise FASTA records back to text. Sequence is line-wrapped at
 * 60 characters per the standard.
 */
export function formatFasta(records: readonly FastaRecord[], lineWidth = 60): string {
  const out: string[] = [];
  for (const r of records) {
    out.push(`>${r.header}`);
    for (let i = 0; i < r.sequence.length; i += lineWidth) {
      out.push(r.sequence.slice(i, i + lineWidth));
    }
  }
  return out.join('\n') + '\n';
}

/**
 * Parse a FASTQ-format string. Strict 4-line records.
 */
export function parseFastq(text: string): FastqRecord[] {
  const lines = text.split(/\r?\n/);
  const records: FastqRecord[] = [];
  for (let i = 0; i + 3 < lines.length; i += 4) {
    if (!lines[i]!.startsWith('@')) continue;
    const header = lines[i]!.slice(1).trim();
    const sequence = lines[i + 1]!.toUpperCase();
    const quality = lines[i + 3]!;
    if (sequence.length !== quality.length) {
      throw new Error(`FASTQ record ${header}: sequence/quality length mismatch`);
    }
    const id = header.split(/\s+/, 1)[0] ?? '';
    records.push({ id, header, sequence, quality });
  }
  return records;
}

/** Reverse-complement a DNA sequence. */
export function reverseComplement(seq: string): string {
  const map: Record<string, string> = {
    A: 'T', T: 'A', C: 'G', G: 'C',
    a: 't', t: 'a', c: 'g', g: 'c',
    N: 'N', n: 'n',
    U: 'A', u: 'a',
    // IUPAC ambiguity
    R: 'Y', Y: 'R', S: 'S', W: 'W', K: 'M', M: 'K',
    B: 'V', V: 'B', D: 'H', H: 'D',
  };
  let out = '';
  for (let i = seq.length - 1; i >= 0; i--) {
    out += map[seq[i]!] ?? 'N';
  }
  return out;
}

/** GC content as a fraction in [0, 1]. Skips N and ambiguity codes. */
export function gcContent(seq: string): number {
  let gc = 0;
  let acgt = 0;
  for (const c of seq) {
    if (c === 'G' || c === 'C' || c === 'g' || c === 'c') { gc++; acgt++; }
    else if (c === 'A' || c === 'T' || c === 'a' || c === 't') acgt++;
  }
  return acgt === 0 ? 0 : gc / acgt;
}

/** Hamming distance between equal-length sequences. */
export function hammingDistance(a: string, b: string): number {
  if (a.length !== b.length) throw new Error(`hamming: lengths differ (${a.length} vs ${b.length})`);
  let d = 0;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) d++;
  return d;
}
